import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { PROMPT_BY_ID, DEFAULT_FOLLOWS } from "@/lib/prompts";
import { api, API, apiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { MiluBrief } from "@/components/MiluBrief";
import { useAuth } from "@/context/AuthContext";
import { useAccent } from "@/context/AccentContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ChatHistory } from "@/components/ChatHistory";
import { READ_VOICES, VoicePreview, SpeedSelect } from "@/components/VoiceControls";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Send, Sparkles, Loader2, ArrowLeft, BookOpen, Wand2, Feather,
  ChevronRight, Volume2, Square, Search, Star,
  Share2, Eraser, Menu, X, Mic, Radio, LayoutGrid, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { EXPLORE_TOOLS, ELITE_TOOLS, PERSONAS, ASSISTANTS, TUTOR_MODES, STORY_MODES,
  CATALOG, FLAT_CATALOG, ALL_MODES, PROMPT_META, parentPersona } from "@/components/chat/personas";
import { StoryCover } from "@/components/chat/StoryCover";


export default function Chat() {
  const { t, lang } = useI18n();
  const { accent } = useAccent();
  const { user, refresh } = useAuth();
  const [params] = useSearchParams();
  const promptId = params.get("prompt");
  const [customPrompt, setCustomPrompt] = useState(null);
  const promptDef = PROMPT_BY_ID[promptId] || (customPrompt?.id === promptId ? customPrompt : null);
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const promptStarterRef = useRef(promptDef?.starter || "");
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState("");
  const [persona, setPersona] = useState(params.get("persona") || "general");
  const [modelInfo, setModelInfo] = useState({ default: "gpt-5.4", models: [] });
  const [usage, setUsage] = useState(null);
  const [assistant, setAssistant] = useState(null);
  const [readAloud, setReadAloud] = useState(localStorage.getItem("cml_read_aloud") === "1");
  const [voice, setVoice] = useState(localStorage.getItem("cml_read_voice") || "nova");
  const [speed, setSpeed] = useState(Number(localStorage.getItem("cml_read_speed")) || 1);
  const [speakingId, setSpeakingId] = useState(null);
  const [query, setQuery] = useState("");
  const [pins, setPins] = useState([]);
  const [labels, setLabels] = useState({});
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showCatalog, setShowCatalog] = useState(null);
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef(null);
  const capRef = useRef(null);
  const handsFreeRef = useRef(false);
  const silenceRef = useRef(null);
  const emptyTriesRef = useRef(0);
  const speechResolveRef = useRef(null);
  const [handsFree, setHandsFree] = useState(false);
  const audioRef = useRef(null);
  const inputRef = useRef(null);

  const openAssistant = async (a) => {
    setAssistant(a);
    setPersona(a.id);
    const empty = sessions.find((s) => s.persona === a.id && !s.message_count);
    if (empty) {
      setActive(empty);
      setMessages([]);
      return;
    }
    const { data } = await api.post("/chat/sessions", { title: a.name, persona: a.id });
    setActive(data);
    setMessages([]);
    await loadSessions();
  };
  const endRef = useRef(null);
  const bootstrapped = useRef(false);

  const loadSessions = useCallback(async () => {
    const { data } = await api.get("/chat/sessions");
    setSessions(data);
    return data;
  }, []);

  useEffect(() => {
    api.get("/chat/models").then(({ data }) => setModelInfo(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (bootstrapped.current) return;
    bootstrapped.current = true;
    (async () => {
      const list = await loadSessions();
      const deepPrompt = params.get("prompt");
      const target0 = deepPrompt && FLAT_CATALOG.find((r) => r.key === deepPrompt);
      if (target0) {
        openCatalogItem(target0.group, target0.item);
        api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
        return;
      }
      const wantedSession = params.get("session");
      const target = wantedSession && list.find((s) => s.session_id === wantedSession);
      if (target) {
        selectSession(target);
        api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
        return;
      }
      const wanted = params.get("persona");
      const reusable = wanted && list.find((s) => s.persona === wanted && !s.message_count);
      if (reusable) selectSession(reusable);
      else if (wanted) await newSession(wanted);
      else {
        // Always land on General, whatever was open last.
        const emptyGeneral = list.find((s) => s.persona === "general" && !s.message_count);
        if (emptyGeneral) selectSession(emptyGeneral);
        else await newSession("general");
      }
      api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!messages.length && !partial) return;
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, partial]);

  useEffect(() => {
    if (!promptId || PROMPT_BY_ID[promptId]) return;
    api
      .get(`/prompts/custom/${promptId}`)
      .then(({ data }) => setCustomPrompt(data))
      .catch(() => toast.error("That prompt is no longer saved"));
  }, [promptId]);

  // Opening a prompt always lands on a clean General chat with that prompt's opener armed.
  useEffect(() => {
    if (!promptDef) return;
    promptStarterRef.current = promptDef.starter;
    setText("");
    setPersona("general");
    setAssistant(null);
    setShowCatalog(false);
    api.post("/prompts/open", { prompt_key: `tool:${promptDef.id}` }).catch(() => {});
    if (messages.length) newSession("general");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [promptDef?.id]);

  const changeModel = async (model) => {
    const before = active.model;
    setActive((prev) => ({ ...prev, model }));
    try {
      await api.patch(`/chat/sessions/${active.session_id}`, { model });
      const picked = modelInfo.models.find((m) => m.id === model);
      toast.success(`This chat now uses ${picked?.label || model}`);
      loadSessions();
    } catch (e) {
      setActive((prev) => ({ ...prev, model: before }));
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const newSession = async (p = persona) => {
    const { data } = await api.post("/chat/sessions", { title: t("newChat"), persona: p });
    setActive(data);
    setMessages([]);
    setAssistant(ALL_MODES.find((a) => a.id === p) || null);
    setSidebarOpen(false);
    await loadSessions();
  };

  const selectSession = async (s) => {
    setSidebarOpen(false);
    setActive(s);
    setPersona(s.persona || "general");
    setAssistant(ALL_MODES.find((a) => a.id === s.persona) || null);
    const { data } = await api.get(`/chat/sessions/${s.session_id}/messages`);
    setMessages(data);
  };

  const removeSession = async (s) => {
    await api.delete(`/chat/sessions/${s.session_id}`);
    const list = await loadSessions();
    if (list.length) selectSession(list[0]);
    else newSession();
  };

  const emptyCount = sessions.filter((s) => !s.message_count && s.session_id !== active?.session_id).length;

  const togglePinned = async (s) => {
    try {
      const { data } = await api.post(`/chat/sessions/${s.session_id}/pin`);
      setSessions((list) =>
        list.map((x) => (x.session_id === s.session_id ? { ...x, pinned: data.pinned } : x)));
      toast.success(data.pinned ? "Pinned to the top" : "Unpinned");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not pin that search");
    }
  };

  const renameSession = async (s, title) => {
    setSessions((list) =>
      list.map((x) => (x.session_id === s.session_id ? { ...x, title } : x)));
    setActive((a) => (a?.session_id === s.session_id ? { ...a, title } : a));
    try {
      await api.post(`/chat/sessions/${s.session_id}/rename`, { title });
    } catch (e) {
      setSessions((list) =>
        list.map((x) => (x.session_id === s.session_id ? { ...x, title: s.title } : x)));
      toast.error(apiError(e.response?.data?.detail) || "Could not rename that search");
    }
  };

  const toggleSaved = async (s) => {
    setSessions((list) =>
      list.map((x) => (x.session_id === s.session_id ? { ...x, saved: !x.saved } : x)));
    try {
      const { data } = await api.post(`/chat/sessions/${s.session_id}/save`);
      toast.success(data.saved ? "Saved to your history" : "Removed from saved");
    } catch (e) {
      setSessions((list) =>
        list.map((x) => (x.session_id === s.session_id ? { ...x, saved: s.saved } : x)));
      toast.error(apiError(e.response?.data?.detail) || "Could not save that search");
    }
  };

  const clearEmpty = async () => {
    try {
      const { data } = await api.delete("/chat/sessions/empty", {
        params: active?.session_id ? { keep: active.session_id } : {},
      });
      await loadSessions();
      toast.success(`Cleared ${data.deleted} empty chat${data.deleted === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not clear those chats");
    }
  };

  const toggleHandsFree = async (on) => {
    setHandsFree(on);
    handsFreeRef.current = on;
    if (!on) {
      stopSpeech();
      stopDictation();
      return;
    }
    const last = [...messages].reverse().find((m) => m.role === "assistant");
    const prompt = last?.content || assistant?.greeting;
    if (!prompt) return;
    await speakText(prompt, "handsfree");
    if (handsFreeRef.current) startDictation();
  };

  const watchSilence = (stream) => {
    clearInterval(silenceRef.current);
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    ctx.createMediaStreamSource(stream).connect(analyser);
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const startedAt = Date.now();
    let quietSince = null;
    silenceRef.current = setInterval(() => {
      analyser.getByteTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128));
      const talking = peak > 8;
      if (talking) quietSince = null;
      else if (!quietSince) quietSince = Date.now();
      const spoke = Date.now() - startedAt > 1200;
      if (spoke && quietSince && Date.now() - quietSince > 2200) {
        clearInterval(silenceRef.current);
        ctx.close().catch(() => {});
        stopDictation();
      }
    }, 200);
  };

  const startDictation = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      const chunks = [];
      recorder.ondataavailable = (e) => e.data.size && chunks.push(e.data);
      recorder.onstop = async () => {
        stream.getTracks().forEach((t2) => t2.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
        if (blob.size < 800) return;
        setTranscribing(true);
        try {
          const form = new FormData();
          form.append("audio", blob, "voice.webm");
          const { data } = await api.post("/chat/transcribe", form);
          if (data.text && handsFreeRef.current) {
            emptyTriesRef.current = 0;
            await send(data.text);
          } else if (data.text) {
            setText((prev) => (prev ? `${prev.replace(/\s+$/, "")} ${data.text}` : data.text));
            inputRef.current?.focus();
          } else {
            toast.info("I couldn't hear anything — try again");
            if (handsFreeRef.current) {
              emptyTriesRef.current += 1;
              if (emptyTriesRef.current >= 3) {
                emptyTriesRef.current = 0;
                toggleHandsFree(false);
                toast.info("Turned hands-free off — tap it again when you're ready");
              } else {
                startDictation();
              }
            }
          }
        } catch (e) {
          emptyTriesRef.current = 0;
          toast.error(apiError(e.response?.data?.detail) || "Could not transcribe that");
        } finally {
          setTranscribing(false);
        }
      };
      recorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      if (handsFreeRef.current) watchSilence(stream);
      capRef.current = setTimeout(() => {
        if (recorderRef.current?.state === "recording") {
          toast.info("That's a long one — wrapping up the recording");
          recorderRef.current.stop();
        }
      }, 60000);
    } catch (err) {
      toast.error(
        err?.name === "NotFoundError"
          ? "No microphone found on this device"
          : "Microphone access is blocked in your browser settings"
      );
    }
  };

  const stopDictation = () => {
    clearTimeout(capRef.current);
    clearInterval(silenceRef.current);
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const promptIntro = promptDef && messages.length === 0 && !partial;
  const catalogOpen = promptIntro
    ? false
    : showCatalog === null
      ? messages.length === 0 && !partial
      : showCatalog;

  const isStoryMode = STORY_MODES.some((m) => m.id === persona);

  const loadPromptState = useCallback(() =>
    api
      .get("/prompts/state")
      .then(({ data }) => {
        setPins(data.pinned || []);
        setLabels(data.labels || {});
      })
      .catch(() => {}), []);

  useEffect(() => {
    loadPromptState();
  }, [loadPromptState]);

  const openCatalogItem = (group, item) => {
    api.post("/prompts/open", { prompt_key: `${group.id}:${item.id}` }).then(loadPromptState).catch(() => {});
    if (item.to) navigate(item.to);
    else openAssistant(item);
  };

  const togglePin = async (key) => {
    const pinned = !pins.includes(key);
    setPins((p) => (pinned ? [...p, key] : p.filter((k) => k !== key)));
    try {
      await api.post("/prompts/pin", { prompt_key: key, pinned });
    } catch {
      loadPromptState();
    }
  };

  const byKey = (key) => FLAT_CATALOG.find((r) => r.key === key);
  const nameFor = (key, item) => labels[key] || item.name || item.label;

  const openRename = (key, item) => {
    setRenaming({ key, original: item.name || item.label });
    setRenameValue(labels[key] || "");
  };

  const saveRename = async (override) => {
    const { key } = renaming;
    const raw = typeof override === "string" ? override : renameValue;
    const label = raw.trim().slice(0, 40);
    setRenaming(null);
    setLabels((l) => {
      const next = { ...l };
      if (label) next[key] = label;
      else delete next[key];
      return next;
    });
    try {
      await api.post("/prompts/label", { prompt_key: key, label });
      toast.success(label ? `Renamed to “${label}”` : "Original name restored");
    } catch (e) {
      loadPromptState();
      toast.error(apiError(e.response?.data?.detail) || "Could not rename that");
    }
  };

  const pinnedItems = pins.map(byKey).filter(Boolean);
  const q = query.trim().toLowerCase();
  const searchResults = q
    ? FLAT_CATALOG.filter((r) =>
        [r.item.name, r.item.label, r.item.blurb, r.group.label, r.item.greeting]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      )
    : [];

  const sharePrompt = async (group, item) => {
    try {
      const { data } = await api.post("/prompts/share", {
        prompt_key: `${group.id}:${item.id}`,
        label: item.name || item.label,
        blurb: item.blurb || "",
        image: item.image || item.avatar || "",
      });
      const url = `${window.location.origin}/api/share/p/${data.share_id}`;
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Share link copied");
      } catch {
        toast.info(url);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Could not create the link");
    }
  };

  const CatalogCard = ({ group, item, prefix }) => {
    const key = `${group.id}:${item.id}`;
    const isPinned = pins.includes(key);
    return (
      <div
        data-testid={`catalog-item-${prefix ? `${prefix}-` : ""}${group.id}-${item.id}`}
        onClick={() => openCatalogItem(group, item)}
        className="group relative flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-panel p-3 text-start transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
      >
        <span className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-surfacealt">
          <img
            src={item.image || item.avatar}
            alt={item.name || item.label}
            width="56"
            height="56"
            loading="lazy"
            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-sm font-bold tracking-tight">
            {nameFor(key, item)}
          </span>
          <span className="block text-xs text-ink-soft">
            {item.blurb || item.greeting?.slice(0, 70) || item.label}
          </span>
        </span>
        {isPinned && (
          <button
            data-testid={`catalog-rename-${group.id}-${item.id}`}
            onClick={(e) => {
              e.stopPropagation();
              openRename(key, item);
            }}
            title="Rename this shortcut"
            className="shrink-0 text-ink-soft transition-colors hover:text-brand"
          >
            <Pencil className="h-4 w-4" />
          </button>
        )}
        <button
          data-testid={`catalog-share-${group.id}-${item.id}`}
          onClick={(e) => {
            e.stopPropagation();
            sharePrompt(group, item);
          }}
          title="Copy a share link"
          className="shrink-0 text-ink-soft transition-colors hover:text-brand"
        >
          <Share2 className="h-4 w-4" />
        </button>
        <button
          data-testid={`catalog-pin-${group.id}-${item.id}`}
          onClick={(e) => {
            e.stopPropagation();
            togglePin(key);
          }}
          title={isPinned ? "Unpin" : "Pin to your shortlist"}
          className={`shrink-0 transition-colors ${isPinned ? "text-forest" : "text-ink-soft hover:text-forest"}`}
        >
          <Star className={`h-4 w-4 ${isPinned ? "fill-current" : ""}`} />
        </button>
      </div>
    );
  };

  const stopSpeech = () => {
    if (speechResolveRef.current) {
      speechResolveRef.current();
      speechResolveRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.pause();
      URL.revokeObjectURL(audioRef.current.src);
      audioRef.current = null;
    }
    setSpeakingId(null);
  };

  const speakText = async (content, id = "live") => {
    stopSpeech();
    setSpeakingId(id);
    try {
      const { data } = await api.post("/chat/speech", { text: content, voice }, { responseType: "blob" });
      const audio = new Audio(URL.createObjectURL(data));
      audio.playbackRate = speed;
      audioRef.current = audio;
      await new Promise((resolve) => {
        speechResolveRef.current = resolve;
        audio.onended = resolve;
        audio.onerror = resolve;
        audio.play().catch(resolve);
      });
      speechResolveRef.current = null;
      setSpeakingId(null);
    } catch (e) {
      stopSpeech();
      toast.error(apiError(e.response?.data?.detail) || "Could not read that aloud");
    }
  };

  useEffect(() => () => {
    handsFreeRef.current = false;
    clearInterval(silenceRef.current);
    clearTimeout(capRef.current);
  }, []);

  useEffect(() => stopSpeech, []);

  const send = async (override) => {
    const raw = typeof override === "string" ? override : text;
    if (!raw.trim() || !active || streaming) return;
    const body = raw.trim();
    const sid = active.session_id;
    setText("");
    setMessages((m) => [...m, { role: "user", content: body, message_id: `tmp-${Date.now()}` }]);
    setStreaming(true);
    setPartial("");
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(localStorage.getItem("cml_token")
            ? { Authorization: `Bearer ${localStorage.getItem("cml_token")}` }
            : {}),
        },
        body: JSON.stringify({
          session_id: active.session_id,
          text: promptStarterRef.current ? `${promptStarterRef.current}\n\n${body}` : body,
          language: lang,
          persona,
        }),
      });
      promptStarterRef.current = "";
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Request failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let full = "";
      let storyId = null;
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.replace(/^data: /, "").trim();
          if (!line) continue;
          const evt = JSON.parse(line);
          if (evt.type === "delta") {
            full += evt.content;
            setPartial(full);
          } else if (evt.type === "story") {
            storyId = evt.story_id;
          } else if (evt.type === "title") {
            const named = evt.title;
            setSessions((list) =>
              list.map((x) => (x.session_id === sid ? { ...x, title: named } : x)));
            setActive((a) => (a?.session_id === sid ? { ...a, title: named } : a));
          } else if (evt.type === "error") {
            toast.error(evt.message);
          }
        }
      }
      setMessages((m) => [
        ...m,
        { role: "assistant", content: full, message_id: `a-${Date.now()}`, story_id: storyId },
      ]);
      setPartial("");
      if (handsFreeRef.current && isStoryMode) {
        await speakText(full, `a-${Date.now()}`);
        const tail = full.trim().split(/(?<=[.!?。！？])\s+/).slice(-2).join(" ");
        if (handsFreeRef.current && /[?？]/.test(tail)) startDictation();
      } else if (readAloud && isStoryMode && full.length > 80) {
        speakText(full);
      }
      loadSessions();
      api.get("/me/usage").then(({ data }) => setUsage(data)).catch(() => {});
      window.dispatchEvent(new Event("chatmilu:chatsent"));
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div
      className="cml-theme mx-auto grid max-w-7xl gap-4 px-4 py-4 sm:px-8 lg:grid-cols-12 lg:gap-6 lg:py-8"
      style={{ "--theme": accent }}
      data-testid="chat-page"
    >
      <MiluBrief />
      {sidebarOpen && (
        <button
          data-testid="sidebar-backdrop"
          aria-label="Close chat list"
          onClick={() => setSidebarOpen(false)}
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
        />
      )}
      <aside
        className={`${
          sidebarOpen
            ? "fixed inset-x-4 top-20 z-40 max-h-[72vh] overflow-y-auto rounded-3xl border border-white/10 bg-panel p-4 shadow-2xl cml-scroll"
            : "hidden"
        } lg:static lg:col-span-3 lg:block lg:max-h-none lg:overflow-visible lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none`}
      >
        <Button
          data-testid="new-chat-button"
          onClick={() => newSession()}
          className="min-h-[44px] w-full rounded-full bg-brand hover:bg-brand-hover active:scale-95"
        >
          <Plus className="me-2 h-4 w-4" /> {t("newSearch")}
        </Button>
        {emptyCount > 1 && (
          <button
            data-testid="clear-empty-chats"
            onClick={clearEmpty}
            className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-full bg-white/5 py-2 text-xs text-ink-soft transition-colors hover:bg-white/10 hover:text-ink"
          >
            <Eraser className="h-3.5 w-3.5" /> Clear {emptyCount} empty chats
          </button>
        )}
        <div className="mt-4">
          <ChatHistory
            sessions={sessions}
            promptMeta={PROMPT_META}
            activeId={active?.session_id}
            onSelect={selectSession}
            onDelete={removeSession}
            onToggleSave={toggleSaved}
            onTogglePin={togglePinned}
            onRename={renameSession}
          />
        </div>
        {usage && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-panel p-4" data-testid="usage-card">
            <p className="text-xs uppercase tracking-widest text-ink-soft">{usage.plan} plan</p>
            <p className="mt-1 text-sm font-semibold">
              {usage.used} / {usage.limit} messages
            </p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-surfacealt">
              <div
                className="h-full bg-brand transition-all"
                style={{ width: `${Math.min(100, (usage.used / usage.limit) * 100)}%` }}
              />
            </div>
          </div>
        )}
      </aside>

      <section className="flex h-[calc(100dvh_-_10.5rem)] min-h-[380px] flex-col overflow-hidden rounded-3xl border border-white/10 bg-panel lg:col-span-9 lg:h-auto lg:min-h-[70vh]">
        <div className="flex flex-wrap items-center gap-3 border-b border-white/10 p-4">
          <button
            data-testid="mobile-sessions-toggle"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle chat list"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-surfacealt text-ink-soft lg:hidden"
          >
            {sidebarOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <div className="flex flex-wrap gap-1.5" data-testid="persona-picker">
            {PERSONAS.map((p) => (
              <button
                key={p.id}
                data-testid={`persona-${p.id}`}
                onClick={() => {
                  setPersona(p.id);
                  if (p.id !== "assistant") setAssistant(null);
                }}
                className={`min-h-[36px] rounded-full px-3.5 text-sm transition-colors ${
                  persona === p.id || parentPersona(persona) === p.id
                    ? "bg-brand text-white"
                    : "bg-surfacealt text-ink-soft hover:text-ink"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="ms-auto flex items-center gap-2">
            <select
              data-testid="model-picker"
              value={active?.model || modelInfo.default}
              onChange={(e) => changeModel(e.target.value)}
              disabled={!active}
              title="Which brain answers this chat"
              className="min-h-[36px] max-w-[11.5rem] rounded-full border border-white/15 bg-panel px-3 text-xs text-ink-soft transition-colors hover:text-ink"
            >
              {modelInfo.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}
                </option>
              ))}
            </select>
            <LanguageSwitcher />
          </div>
        </div>

        {persona === "assistant" && (
          <div className="max-h-[26vh] min-h-0 shrink basis-auto overflow-y-auto border-b lg:max-h-[45vh] border-white/10 bg-brand/5 p-4 cml-scroll" data-testid="assistant-tools">
            <p className="text-xs uppercase tracking-widest text-ink-soft">Assistants</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
              {ASSISTANTS.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.id}
                    data-testid={`assistant-${a.id}`}
                    onClick={() => openAssistant(a)}
                    className="group flex flex-col items-start gap-2 rounded-2xl border border-white/10 bg-panel p-4 text-start transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
                  >
                    <span className="inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl bg-surfacealt text-forest ring-1 ring-white/10 transition-colors group-hover:bg-brand group-hover:text-white">
                      {a.avatar ? (
                        <img
                          src={a.avatar}
                          alt={a.name}
                          data-testid={`assistant-image-${a.id}`}
                          className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                      ) : (
                        <Icon className="h-5 w-5" />
                      )}
                    </span>
                    <span className="font-display text-sm font-bold tracking-tight">{a.name}</span>

                  </button>
                );
              })}
            </div>
          </div>
        )}

        {persona === "general" && !assistant && !promptIntro && (
          <button
            data-testid="browse-prompts-toggle"
            onClick={() => setShowCatalog(!catalogOpen)}
            className="flex w-full items-center gap-2 border-b border-white/10 bg-brand/5 px-5 py-2.5 text-start text-xs text-brand hover:bg-brand/10"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            {catalogOpen ? "Hide all prompts" : "Browse all prompts — Elite Tools, Storytelling, Explore, Assistants, Tutor"}
          </button>
        )}

        {assistant && (
          <div className="flex items-center gap-3 border-b border-white/10 px-5 py-3" data-testid="assistant-header">
            <button
              data-testid="assistant-back"
              onClick={() => {
                setAssistant(null);
                setPersona(parentPersona(assistant.id));
              }}
              className="text-ink-soft hover:text-brand"
            >
              <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
            </button>
            {assistant.avatar && (
              <img
                src={assistant.avatar}
                alt={assistant.name}
                className="h-8 w-8 rounded-full border border-white/10 object-cover"
              />
            )}
            <p className="font-display text-base font-bold tracking-tight text-forest">{assistant.name}</p>
            {isStoryMode && (
              <div className="ms-auto flex flex-wrap items-center gap-3" data-testid="read-aloud-controls">
                <label className="flex items-center gap-2 text-xs text-ink-soft">
                  <Radio className="h-3.5 w-3.5" /> Hands-free
                  <Switch
                    data-testid="hands-free-toggle"
                    checked={handsFree}
                    onCheckedChange={toggleHandsFree}
                  />
                </label>
                <label className="flex items-center gap-2 text-xs text-ink-soft">
                  <Volume2 className="h-3.5 w-3.5" /> Read aloud
                  <Switch
                    data-testid="read-aloud-toggle"
                    checked={readAloud}
                    onCheckedChange={(v) => {
                      setReadAloud(v);
                      localStorage.setItem("cml_read_aloud", v ? "1" : "0");
                      if (!v) stopSpeech();
                      else if (!messages.length && assistant.greeting) speakText(assistant.greeting, "greeting");
                    }}
                  />
                </label>
                {readAloud && (
                  <>
                    <Select
                      value={voice}
                      onValueChange={(v) => {
                        setVoice(v);
                        localStorage.setItem("cml_read_voice", v);
                      }}
                    >
                      <SelectTrigger data-testid="read-aloud-voice" className="h-9 w-[132px] rounded-full text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {READ_VOICES.map((v) => (
                          <SelectItem key={v.id} value={v.id} data-testid={`read-voice-${v.id}`}>
                            {v.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <VoicePreview voice={voice} testid="read-aloud-preview" />
                    <SpeedSelect value={speed} onChange={setSpeed} testid="read-aloud-speed" />
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {persona === "tutor" && (
          <div className="max-h-[26vh] min-h-0 shrink basis-auto overflow-y-auto border-b lg:max-h-[45vh] border-white/10 bg-brand/5 p-4 cml-scroll" data-testid="tutor-tools">
            <p className="inline-flex items-center gap-2 font-display text-base font-bold tracking-tight">
              <BookOpen className="h-4 w-4 text-forest" /> AI Tutor
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              Learn smarter with interactive sessions designed to support and educate.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {TUTOR_MODES.map((m) => (
                <button
                  key={m.id}
                  data-testid={`tutor-${m.id}`}
                  onClick={() => openAssistant(m)}
                  className="group flex flex-col items-start gap-2 rounded-2xl border border-white/10 bg-panel p-4 text-start transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
                >
                  <img
                    src={m.image}
                    alt=""
                    loading="lazy"
                    data-testid={`tutor-image-${m.id}`}
                    className="h-14 w-14 rounded-xl object-cover ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105"
                  />
                  <span className="font-display text-sm font-bold tracking-tight">{m.name}</span>
                  <span className="text-xs text-ink-soft">{m.blurb}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {persona === "storyteller" && (
          <div className="max-h-[26vh] min-h-0 shrink basis-auto overflow-y-auto border-b lg:max-h-[45vh] border-white/10 bg-brand/5 p-4 cml-scroll" data-testid="story-tools">
            <p className="inline-flex items-center gap-2 font-display text-base font-bold tracking-tight">
              <Feather className="h-4 w-4 text-forest" /> Storytelling
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              Pick a style and I'll interview you, then write the whole thing.{" "}
              <Link to="/stories" data-testid="story-library-link" className="text-brand hover:underline">
                Story Library →
              </Link>
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {STORY_MODES.map((m) => (
                <button
                  key={m.id}
                  data-testid={`story-${m.id}`}
                  onClick={() => openAssistant(m)}
                  className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-panel p-3 text-start transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
                >
                  <span className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-surfacealt ring-1 ring-white/10">
                    <img
                      src={m.avatar}
                      alt={m.name}
                      loading="lazy"
                      data-testid={`story-image-${m.id}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-display text-sm font-bold tracking-tight">{m.name}</span>
                    <span className="block text-xs text-ink-soft">{m.blurb}</span>
                  </span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-ink-soft transition-colors group-hover:text-brand rtl:rotate-180" />
                </button>
              ))}
            </div>
          </div>
        )}

        {persona === "explore" && (
          <div className="max-h-[26vh] min-h-0 shrink basis-auto overflow-y-auto border-b lg:max-h-[45vh] border-white/10 bg-brand/5 p-4 cml-scroll" data-testid="explore-tools">
            <p className="text-xs uppercase tracking-widest text-ink-soft">Explore tools</p>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              {EXPLORE_TOOLS.map((tool) => (
                <Link
                  key={tool.id}
                  to={tool.to}
                  data-testid={`explore-tool-${tool.id}`}
                  className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-panel p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
                >
                  <img
                    src={tool.image}
                    alt=""
                    loading="lazy"
                    data-testid={`explore-image-${tool.id}`}
                    className="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105"
                  />
                  <span>
                    <span className="block font-display text-sm font-bold tracking-tight">{tool.label}</span>
                    <span className="block text-xs text-ink-soft">{tool.blurb}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {persona === "elite" && (
          <div className="max-h-[26vh] min-h-0 shrink basis-auto overflow-y-auto border-b lg:max-h-[45vh] border-white/10 bg-brand/5 p-4 cml-scroll" data-testid="elite-tools">
            <p className="inline-flex items-center gap-2 font-display text-base font-bold tracking-tight">
              <Wand2 className="h-4 w-4 text-forest" /> Elite Tools
            </p>
            <p className="mt-1 text-sm text-ink-soft">
              Studio-grade generators — images, brand logos, tattoo designs and AI text detection.
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
              {ELITE_TOOLS.map((tool) => (
                <Link
                  key={tool.id}
                  to={tool.to}
                  data-testid={`elite-tool-${tool.id}`}
                  className="group flex items-start gap-3 rounded-2xl border border-white/10 bg-panel p-4 transition-all duration-300 hover:-translate-y-0.5 hover:border-brand/40"
                >
                  <img
                    src={tool.image}
                    alt=""
                    loading="lazy"
                    data-testid={`elite-image-${tool.id}`}
                    className="h-12 w-12 shrink-0 rounded-xl object-cover ring-1 ring-white/10 transition-transform duration-300 group-hover:scale-105"
                  />
                  <span>
                    <span className="block font-display text-sm font-bold tracking-tight">{tool.label}</span>
                    <span className="block text-xs text-ink-soft">{tool.blurb}</span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-5 cml-scroll" data-testid="chat-messages">
          {messages.length === 0 && !partial && assistant && (
            <div className="space-y-4" data-testid="assistant-greeting">
              <div className="flex justify-start">
                <div className="max-w-[85%] rounded-3xl rounded-bl-sm border border-white/10 bg-panel p-4">
                  <p className="flex items-center gap-2.5 font-display text-sm font-bold tracking-tight text-forest">
                    {assistant.avatar ? (
                      <img
                        src={assistant.avatar}
                        alt={assistant.name}
                        className="h-9 w-9 rounded-full border border-white/10 object-cover"
                      />
                    ) : (
                      <assistant.icon className="h-4 w-4" />
                    )}
                    {assistant.name}
                  </p>
                  <p dir="auto" className="mt-2 text-sm leading-relaxed">{assistant.greeting}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2" role="group" aria-label="Suggested starters" data-testid="assistant-starters">
                {(assistant.starters || []).map((s, i) => (
                  <button
                    key={`${i}-${s.slice(0, 24)}`}
                    data-testid={`starter-${i}`}
                    aria-label={`Use starter: ${s}`}
                    onClick={() => {
                      setText(s);
                      inputRef.current?.focus();
                    }}
                    dir="auto"
                    className="rounded-full border border-white/15 bg-panel px-4 py-2 text-sm text-ink-soft transition-colors hover:border-brand hover:text-ink"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {promptIntro && (
            <div className="flex flex-col items-center py-10 text-center" data-testid="prompt-intro">
              {promptDef.tile ? (
                <img
                  src={promptDef.tile}
                  alt=""
                  data-testid="prompt-intro-tile"
                  className="h-24 w-24 rounded-3xl border border-white/10 object-cover shadow-lg shadow-black/40"
                />
              ) : (
                <Sparkles className="h-10 w-10 text-brand" />
              )}
              <p className="mt-5 font-display text-2xl font-extrabold tracking-tighter" data-testid="prompt-intro-title">
                {promptDef.label}
              </p>
              <p
                dir="auto"
                data-testid="prompt-intro-ask"
                className="mt-3 max-w-md text-base leading-relaxed text-ink-soft"
              >
                {promptDef.ask || promptDef.blurb}
              </p>
            </div>
          )}
          {messages.length === 0 && !partial && !assistant && !promptIntro && (
            <div className="pt-2">
              <div className="flex flex-col items-center py-2 text-center">
                <Sparkles className="h-8 w-8 text-brand" />
                <p className="mt-2 font-display text-xl font-bold tracking-tight">
                  {user?.name ? `Hello, ${user.name}` : "Hello"}
                </p>
                <p dir="auto" className="mt-1 max-w-sm text-sm text-ink-soft">
                  {t("askAnything")}
                </p>
              </div>
            </div>
          )}

          {persona === "general" && !assistant && catalogOpen && (
                <div className="space-y-5 pb-4 pt-0" data-testid="general-catalog">
                  <div className="mx-auto max-w-md">
                    <div className="relative">
                      <Search className="pointer-events-none absolute start-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-soft" />
                      <Input
                        data-testid="catalog-search"
                        dir="auto"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Search prompts — tattoo, homework, bedtime…"
                        className="h-12 rounded-full ps-11"
                      />
                    </div>
                  </div>

                  {query.trim() ? (
                    <section data-testid="catalog-results">
                      <h3 className="font-display text-base font-bold tracking-tight md:text-lg">
                        {searchResults.length} result{searchResults.length === 1 ? "" : "s"}
                      </h3>
                      {searchResults.length === 0 && (
                        <p data-testid="catalog-no-results" className="mt-2 text-sm text-ink-soft">
                          Nothing matched “{query}”. Try “story”, “logo” or “summarize”.
                        </p>
                      )}
                      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {searchResults.map((r) => (
                          <CatalogCard key={r.key} group={r.group} item={r.item} prefix="result" />
                        ))}
                      </div>
                    </section>
                  ) : (
                    <>
                      {pinnedItems.length > 0 && (
                        <section data-testid="catalog-section-pinned">
                          <h3 className="inline-flex items-center gap-2 font-display text-base font-bold tracking-tight md:text-lg">
                            <Star className="h-4 w-4 text-forest" /> Pinned
                          </h3>
                          <p className="mt-0.5 text-sm text-ink-soft">Your personal shortlist</p>
                          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {pinnedItems.map((r) => (
                              <CatalogCard key={r.key} group={r.group} item={r.item} prefix="pinned" />
                            ))}
                          </div>
                        </section>
                      )}

                      {CATALOG.map((group) => (
                        <section key={group.id} data-testid={`catalog-section-${group.id}`}>
                          <div className="flex items-end justify-between gap-4">
                            <div>
                              <h3 className="font-display text-base font-bold tracking-tight md:text-lg">
                                {group.label}
                              </h3>
                              <p className="mt-0.5 text-sm text-ink-soft">{group.blurb}</p>
                            </div>
                            <button
                              data-testid={`catalog-open-${group.id}`}
                              onClick={() => setPersona(group.tab)}
                              className="shrink-0 text-xs text-brand hover:underline"
                            >
                              Open tab →
                            </button>
                          </div>
                          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {group.items.map((item) => (
                              <CatalogCard key={`${group.id}-${item.id}`} group={group} item={item} />
                            ))}
                          </div>
                        </section>
                      ))}
                    </>
                  )}
                </div>
          )}
          {messages.map((m, i) => (
            <div key={m.message_id || i}>
              <div
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                data-testid={`message-${m.role}-${i}`}
              >
                <div
                  dir="auto"
                  className={`max-w-[85%] whitespace-pre-wrap rounded-3xl p-4 text-sm leading-relaxed ${
                    m.role === "user"
                      ? "rounded-br-sm bg-brand text-white"
                      : "rounded-bl-sm border border-white/10 bg-page"
                  }`}
                >
                  {m.content}
                </div>
              </div>
              {m.role === "assistant" && isStoryMode && m.content?.length > 80 && (
                <div className="mt-2 flex justify-start">
                  <button
                    data-testid={`speak-message-${i}`}
                    onClick={() =>
                      speakingId === (m.message_id || i)
                        ? stopSpeech()
                        : speakText(m.content, m.message_id || i)
                    }
                    className="inline-flex items-center gap-2 rounded-full bg-white/5 px-3 py-1.5 text-xs text-ink-soft transition-colors hover:bg-white/10 hover:text-brand"
                  >
                    {speakingId === (m.message_id || i) ? (
                      <>
                        <Square className="h-3 w-3" /> Stop
                      </>
                    ) : (
                      <>
                        <Volume2 className="h-3.5 w-3.5" /> Listen
                      </>
                    )}
                  </button>
                </div>
              )}
              {m.story_id && <StoryCover storyId={m.story_id} />}
            </div>
          ))}
          {partial && (
            <div className="flex justify-start" data-testid="message-streaming">
              <div
                dir="auto"
                className="max-w-[85%] whitespace-pre-wrap rounded-3xl rounded-bl-sm border border-white/10 bg-page p-4 text-sm leading-relaxed"
              >
                {partial}
              </div>
            </div>
          )}
          {streaming && !partial && (
            <Loader2 className="h-4 w-4 animate-spin text-brand" data-testid="chat-loading" />
          )}
          {promptDef && !streaming && !partial && messages.length >= 2 &&
            messages[messages.length - 1].role === "assistant" && (
            <div className="flex flex-wrap gap-2" data-testid="prompt-followups">
              {(promptDef.follows || DEFAULT_FOLLOWS).map((f, i) => (
                <button
                  key={f}
                  data-testid={`prompt-followup-${i}`}
                  onClick={() => send(f)}
                  className="rounded-full border border-brand/40 bg-brand/10 px-4 py-2 text-sm text-brand transition-colors hover:bg-brand/20 active:scale-95"
                >
                  {f}
                </button>
              ))}
            </div>
          )}
          <div ref={endRef} />
        </div>

        {pinnedItems.length > 0 && (
          <div
            className="flex items-center gap-2 overflow-x-auto border-t border-white/10 px-4 py-2.5 cml-scroll"
            data-testid="quick-prompt-bar"
          >
            <Star className="h-3.5 w-3.5 shrink-0 text-forest" />
            {pinnedItems.map(({ key, group, item }) => (
              <span
                key={key}
                className="group inline-flex shrink-0 items-center rounded-full bg-surfacealt transition-colors hover:bg-white/15"
              >
                <button
                  data-testid={`quick-prompt-${group.id}-${item.id}`}
                  onClick={() => openCatalogItem(group, item)}
                  title={item.blurb || item.label}
                  className="inline-flex items-center gap-2 py-1.5 ps-1.5 text-xs text-ink-soft transition-colors group-hover:text-ink"
                >
                  <img
                    src={item.image || item.avatar}
                    alt=""
                    width="24"
                    height="24"
                    className="h-6 w-6 rounded-full object-cover"
                  />
                  {nameFor(key, item)}
                </button>
                <button
                  data-testid={`quick-rename-${group.id}-${item.id}`}
                  onClick={() => openRename(key, item)}
                  title="Rename this shortcut"
                  className="px-2.5 py-1.5 text-ink-soft opacity-0 transition-opacity hover:text-brand focus:opacity-100 group-hover:opacity-100"
                >
                  <Pencil className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        )}

        {handsFree && (
          <div
            data-testid="hands-free-status"
            className="flex items-center gap-2 border-t border-white/10 bg-brand/10 px-4 py-2 text-xs text-brand"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-brand opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-brand" />
            </span>
            {streaming
              ? "Writing your story…"
              : transcribing
                ? "Got it — working out what you said…"
                : recording
                  ? "Listening… just talk, I'll wait for you to finish"
                  : speakingId
                    ? "Reading aloud…"
                    : "Hands-free is on"}
          </div>
        )}

        <div className="flex items-end gap-3 border-t border-white/10 p-4">
          <Textarea
            ref={inputRef}
            data-testid="chat-input"
            dir="auto"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={
              recording
                ? "Listening… tap the mic to stop"
                : promptDef || assistant
                  ? "Type all necessary details"
                  : t("askAnything")
            }
            className="min-h-[52px] max-h-40 flex-1 resize-none rounded-2xl"
          />
          <Button
            data-testid="chat-mic-button"
            onClick={recording ? stopDictation : startDictation}
            disabled={transcribing}
            title={transcribing ? "Transcribing…" : recording ? "Stop dictating" : "Dictate your answer"}
            className={`min-h-[52px] shrink-0 rounded-full px-4 ${
              recording ? "bg-destructive text-white hover:bg-destructive/90" : "bg-surfacealt text-ink-soft hover:bg-white/15"
            }`}
          >
            {transcribing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : recording ? (
              <Square className="h-4 w-4" />
            ) : (
              <Mic className="h-4 w-4" />
            )}
          </Button>
          <Button
            data-testid="chat-send-button"
            onClick={send}
            disabled={streaming || !text.trim()}
            className="min-h-[52px] rounded-full bg-brand px-5 hover:bg-brand-hover active:scale-95"
          >
            <Send className="h-4 w-4 rtl:rotate-180" />
            <span className="ms-2 hidden sm:inline">{t("send")}</span>
          </Button>
        </div>
      </section>
      {renaming && (
        <Dialog open onOpenChange={(o) => !o && setRenaming(null)}>
          <DialogContent className="sm:max-w-sm" data-testid="rename-prompt-dialog">
            <DialogHeader>
              <DialogTitle className="font-display tracking-tight">Rename this shortcut</DialogTitle>
              <DialogDescription className="text-xs text-ink-soft">
                Only you see this name. Originally “{renaming.original}”.
              </DialogDescription>
            </DialogHeader>
            <Input
              data-testid="rename-prompt-input"
              autoFocus
              maxLength={40}
              placeholder={renaming.original}
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && saveRename()}
              className="min-h-[46px] rounded-xl"
            />
            <div className="flex gap-2">
              <Button
                data-testid="rename-prompt-save"
                onClick={() => saveRename()}
                className="min-h-[44px] flex-1 rounded-full bg-brand hover:bg-brand-hover active:scale-95"
              >
                Save name
              </Button>
              <Button
                data-testid="rename-prompt-reset"
                variant="outline"
                onClick={() => saveRename("")}
                className="min-h-[44px] rounded-full px-5"
              >
                Reset
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
